import React, { useCallback, useEffect, useRef, useState } from 'react'
import { detectIsIOS, isStandaloneDisplayMode } from '../../lib/deviceDetection'
import type { ViewMode } from '../../types'

const DEFAULT_APP_STORE_URL =
  'https://apps.apple.com/us/app/insight-atlas-deep-research/id6755838727'
const DISMISS_STORAGE_KEY = 'insightatlas.iosAppBanner.dismissed'
const DEFAULT_AUTO_DISMISS_MS = 15000

function readDismissedState(): boolean {
  if (typeof window === 'undefined') {
    return true
  }

  try {
    return window.sessionStorage.getItem(DISMISS_STORAGE_KEY) === '1'
  } catch {
    return false
  }
}

function persistDismissedState(): void {
  if (typeof window === 'undefined') {
    return
  }

  try {
    window.sessionStorage.setItem(DISMISS_STORAGE_KEY, '1')
  } catch {
    // Ignore storage write failures and just keep the local hidden state.
  }
}

interface MobileAppBannerProps {
  viewMode: ViewMode
  bottomOffsetPx?: number
  autoDismissMs?: number
  appStoreUrl?: string
}

export default function MobileAppBanner({
  viewMode,
  bottomOffsetPx = 24,
  autoDismissMs = DEFAULT_AUTO_DISMISS_MS,
  appStoreUrl = DEFAULT_APP_STORE_URL,
}: MobileAppBannerProps) {
  const bannerRef = useRef<HTMLDivElement>(null)
  const [dismissed, setDismissed] = useState(readDismissedState)

  const dismissBanner = useCallback(() => {
    persistDismissedState()
    setDismissed(true)
  }, [])

  const isEligible =
    viewMode === 'mobile' && detectIsIOS() && !isStandaloneDisplayMode()

  useEffect(() => {
    if (!isEligible || dismissed) {
      return
    }

    const timerId = window.setTimeout(() => {
      dismissBanner()
    }, autoDismissMs)

    return () => {
      window.clearTimeout(timerId)
    }
  }, [autoDismissMs, dismissBanner, dismissed, isEligible])

  useEffect(() => {
    if (!isEligible || dismissed) {
      return
    }

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null
      if (!bannerRef.current || !target || bannerRef.current.contains(target)) {
        return
      }

      dismissBanner()
    }

    document.addEventListener('pointerdown', handlePointerDown)
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown)
    }
  }, [dismissBanner, dismissed, isEligible])

  if (!isEligible || dismissed) {
    return null
  }

  return (
    <div
      className="fixed left-3 right-3 z-[120] sm:left-4 sm:right-4"
      data-testid="mobile-app-banner"
      style={{
        bottom: `calc(env(safe-area-inset-bottom, 0px) + ${bottomOffsetPx}px)`,
      }}
    >
      <div
        ref={bannerRef}
        className="relative overflow-hidden rounded-[28px] border border-white/70 bg-white/95 px-4 py-4 text-gray-900 shadow-[0_24px_70px_rgba(15,23,42,0.22)] backdrop-blur-xl"
      >
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-slate-100/90 via-slate-50/35 to-transparent"
        />

        <div className="relative flex items-start gap-3.5">
          <img
            src="/logo192.png"
            alt="Insight Atlas"
            className="h-14 w-14 flex-shrink-0 rounded-[18px] shadow-[0_14px_32px_rgba(15,23,42,0.18)]"
          />
          <div className="min-w-0 flex-1 pr-1">
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-[0.68rem] font-semibold uppercase tracking-[0.18em] text-slate-500">
                Insight Atlas for iPhone
              </p>
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[0.68rem] font-medium text-slate-500">
                App Store
              </span>
            </div>
            <p className="mt-1 text-[1.02rem] font-semibold leading-5 text-slate-950">
              Deep research works better in the app.
            </p>
            <p className="mt-1.5 text-[0.94rem] leading-5 text-slate-600">
              Open Insight Atlas in the native iPhone app for the best experience.
            </p>
          </div>
          <button
            aria-label="Dismiss app banner"
            className="mt-0.5 rounded-full p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
            onClick={dismissBanner}
            type="button"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="relative mt-4 grid grid-cols-2 gap-2.5">
          <button
            className="min-h-[46px] rounded-[18px] border border-slate-200 bg-white px-4 py-2.5 text-[0.95rem] font-medium text-slate-700 transition-colors hover:bg-slate-50"
            onClick={dismissBanner}
            type="button"
          >
            Continue in browser
          </button>
          <a
            className="inline-flex min-h-[46px] items-center justify-center gap-1.5 rounded-[18px] bg-blue-600 px-4 py-2.5 text-center text-[0.95rem] font-semibold text-white shadow-[0_14px_28px_rgba(37,99,235,0.28)] transition-colors hover:bg-blue-500"
            href={appStoreUrl}
            onClick={dismissBanner}
            rel="noreferrer"
          >
            Get the app
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </a>
        </div>
      </div>
    </div>
  )
}
